import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { libraryPeopleQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { CreateEntityPerson, EntityPerson } from '@/types/library/Entity'

interface UseCreatePersonArgs {
  orgId: UUID | ''
}

const createPerson = async (
  orgId: UUID | '',
  data: CreateEntityPerson,
  accessToken: string,
): Promise<EntityPerson> => {
  if (!orgId) {
    throw new Error('Organization ID is required')
  }

  const requestBody = data

  const res = await fetch(`${API_URL}/api/v1/organizations/${orgId}/entities`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }

  return (await res.json()) as EntityPerson
}

/**
 * Creates a new person entity
 */
export const useCreatePerson = ({ orgId }: UseCreatePersonArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<EntityPerson, Error, CreateEntityPerson, unknown>({
    mutationFn: (data) => createPerson(orgId, data, accessToken),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: libraryPeopleQueryKeys.organization({ orgId }),
      }),
    onError: (error) => {
      handleError(error)
    },
  })
}
